import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';

interface AnimatedTextProps {
  htmlContent: string;
  speed?: number; // milliseconds per word
}

const AnimatedText: React.FC<AnimatedTextProps> = ({ htmlContent, speed = 50 }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isRevealing, setIsRevealing] = useState(true);

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;

    // Parse HTML and wrap each word in a span
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;

    const processNode = (node: Node): Node => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || '';
        const words = text.split(/(\s+)/); // Split by whitespace but keep the whitespace

        const fragment = document.createDocumentFragment();
        words.forEach((word, index) => {
          if (word.trim()) {
            // Create a span for each word
            const span = document.createElement('span');
            span.textContent = word;
            span.style.opacity = '0';
            span.setAttribute('data-word-index', String(index));
            fragment.appendChild(span);
          } else if (word) {
            // Preserve whitespace as text node
            fragment.appendChild(document.createTextNode(word));
          }
        });

        return fragment;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node.cloneNode(false) as Element;
        Array.from(node.childNodes).forEach(child => {
          element.appendChild(processNode(child));
        });
        return element;
      }

      return node.cloneNode(true);
    };

    // Process all child nodes
    const processedContent = document.createDocumentFragment();
    Array.from(tempDiv.childNodes).forEach(child => {
      processedContent.appendChild(processNode(child));
    });

    // Clear container and add processed content
    container.innerHTML = '';
    container.appendChild(processedContent);

    // Get all word spans
    const wordSpans = container.querySelectorAll('span[data-word-index]');

    // Animate words appearing one by one
    let currentIndex = 0;
    const revealNextWord = () => {
      if (currentIndex < wordSpans.length) {
        const span = wordSpans[currentIndex] as HTMLElement;
        span.style.transition = 'opacity 0.1s ease-in';
        span.style.opacity = '1';
        currentIndex++;
        setTimeout(revealNextWord, speed);
      } else {
        setIsRevealing(false);
      }
    };

    revealNextWord();

    // Cleanup function
    return () => {
      setIsRevealing(false);
    };
  }, [htmlContent, speed]);

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="animated-text-content"
    />
  );
};

export default AnimatedText;
